import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconComponent } from 'continity-shared-ui';

/**
 * Recursive renderer for the shared legal translation trees (`legalConsent.agb.sections`,
 * `privacyPolicy.sections`). The MAIN portal renders these trees with two hand-rolled copies of
 * the same recursion (legal page + consent dialog); this app renders them ONCE, with a `variant`
 * for the two visual contexts:
 *
 *   - `page`   — the standalone public legal pages: level-0 sections as cards with an accent
 *                bar (mirrors the main portal's /privacy-consent and /legal-consent pages).
 *   - `dialog` — the compact form used inside the consent-gate dialog's scrollboxes (mirrors
 *                the main portal's privacy-consent-dialog section template).
 *
 * The section keys are DERIVED from the translation object at runtime (Object.keys) instead of
 * hardcoding the section count the way the main portal does (13/15) — a new section in the
 * shared bundle shows up here without a code change, and integer-like keys enumerate in
 * ascending numeric order per the JS spec. The i18n-parity spec pins that every section carries
 * a title in all four locales.
 *
 * Expansion state is stored INVERTED (a collapsed-set instead of an expanded-set): translations
 * load async, so the section keys are unknown at construction time — "expanded unless explicitly
 * collapsed" gives the default-expanded behaviour without seeding.
 */
@Component({
  selector: 'phoenix-legal-sections',
  imports: [CommonModule, TranslateModule, IconComponent],
  template: `
    <div [class]="variant === 'page' ? 'space-y-6' : 'space-y-4 text-sm leading-relaxed'">
      @for (k of sectionKeys(); track k) {
        <ng-container
          *ngTemplateOutlet="sectionTemplate; context: {
            basePath: rootKey,
            key: k,
            level: 0,
            sectionPath: k
          }"></ng-container>
      }
    </div>

    <!-- Recursive template for nested sections (same shape as the main portal's renderers) -->
    <ng-template #sectionTemplate let-basePath="basePath" let-key="key" let-level="level" let-sectionPath="sectionPath">
      <section
        [class]="
          variant === 'page' && level === 0
            ? 'rounded-2xl border bg-white text-slate-800 dark:bg-slate-900 dark:text-slate-200 border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow p-6'
            : ''
        "
        [style.margin-left.rem]="level * (variant === 'page' ? 2 : 1)">
        <div class="flex items-center gap-3" [class.mb-4]="variant === 'page'" [class.mb-1]="variant === 'dialog'">
          @if (variant === 'page' && level === 0) {
            <span class="inline-block h-5 w-1.5 rounded" [class]="accentClass"></span>
          }

          <h3
            class="font-semibold flex-1"
            [class.text-slate-900]="variant === 'page'"
            [class.dark:text-white]="variant === 'page'"
            [class.text-lg]="variant === 'page' && level === 0"
            [class.text-base]="(variant === 'page' && level === 1) || (variant === 'dialog' && level === 0)"
            [class.text-sm]="(variant === 'page' && level > 1) || (variant === 'dialog' && level > 0)">
            {{ (basePath + '.' + key + '.title') | translate }}
          </h3>

          @if (hasNestedSections(basePath, key)) {
            <button
              type="button"
              (click)="toggleSection(sectionPath)"
              class="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors shrink-0">
              <app-icon
                [name]="isSectionExpanded(sectionPath) ? 'chevron-up' : 'chevron-down'"
                [size]="variant === 'page' ? 20 : 16">
              </app-icon>
            </button>
          }
        </div>

        <!-- Body (only when the key resolves — a missing body must not echo the raw key) -->
        @if ((basePath + '.' + key + '.body') | translate; as body) {
          @if (body !== basePath + '.' + key + '.body') {
            <div
              class="whitespace-pre-line"
              [class.text-sm]="variant === 'page'"
              [class.leading-relaxed]="variant === 'page'"
              [class.opacity-90]="variant === 'dialog'"
              [class.mt-1]="variant === 'dialog'">
              {{ body }}
            </div>
          }
        }

        @if (hasNestedSections(basePath, key) && isSectionExpanded(sectionPath)) {
          <div [class]="variant === 'page' ? 'mt-4 space-y-4' : 'mt-3 space-y-3'">
            @for (nestedKey of nestedSectionKeys(basePath, key); track nestedKey) {
              <ng-container
                *ngTemplateOutlet="sectionTemplate; context: {
                  basePath: basePath + '.' + key + '.sections',
                  key: nestedKey,
                  level: level + 1,
                  sectionPath: sectionPath + '.' + nestedKey
                }"></ng-container>
            }
          </div>
        }
      </section>
    </ng-template>
  `,
})
export class LegalSectionsComponent {
  private readonly translate = inject(TranslateService);

  /** Translation OBJECT node whose children are the sections, e.g. `privacyPolicy.sections`. */
  @Input({ required: true }) rootKey!: string;
  /** Visual context — see class docs. */
  @Input() variant: 'page' | 'dialog' = 'page';
  /** Accent-bar Tailwind class for level-0 page cards (main portal: emerald=privacy, sky=terms). */
  @Input() accentClass = 'bg-emerald-500';

  /** Collapsed-set (inverted expansion state — see class docs). */
  private readonly collapsedSections = new Set<string>();

  sectionKeys(): string[] {
    return this.childKeys(this.rootKey);
  }

  hasNestedSections(basePath: string, key: string): boolean {
    return this.childKeys(`${basePath}.${key}.sections`).length > 0;
  }

  nestedSectionKeys(basePath: string, key: string): string[] {
    return this.childKeys(`${basePath}.${key}.sections`);
  }

  toggleSection(sectionPath: string): void {
    if (this.collapsedSections.has(sectionPath)) {
      this.collapsedSections.delete(sectionPath);
    } else {
      this.collapsedSections.add(sectionPath);
    }
  }

  isSectionExpanded(sectionPath: string): boolean {
    return !this.collapsedSections.has(sectionPath);
  }

  /** Object-node children of a translation path; [] while translations are still loading. */
  private childKeys(path: string): string[] {
    const value = this.translate.instant(path) as unknown;
    if (value === path || value === null || typeof value !== 'object') return [];
    return Object.keys(value);
  }
}
